import { IApiBaseProps, ISortByQuery } from '@common/api.models';
import { createSearchAsyncFn } from '@common/search.services';
import { envConfigs } from '@config/envConfigs';
import {
  createDownloadFileAsyncFn,
  createDownloadFileWithCustomPropsAsyncFn,
  createDownloadFileWithCustomPropsAsyncFnGet,
} from '@file/file.services';
import { IFileResponse, IPdfFileDownloadRequest } from '@file/projectFile/projectFile.models';
import { IItemResponse, IPagedResultsV1 } from '@gcl/react-core';
import { IRevision } from '@revision/revision.models';
import { apiServerGet, apiServerPost, apiServerPut } from '@common/axios.services';
import { IDownloadFileBaseProps, IDownloadFileProgressProps } from '@file/file.models';
import {
  IProjectDesignModificationSearchFilter,
  IProjectDesignModificationSearchResult,
  IProjectDesignModification,
  IUpdateProjectDesignModificationRequest,
  IUpdateProjectDesignModificationStatusRequest,
  IApproveProjectDesignModificationRequest,
  IProjectDesignModificationOptionForDesignerTransmittal,
  IProjectDesignModificationOptionForDesignerTransmittalRequest,
  ISearchDesignModificationDrawingRequest,
  ICreateProjectDesignModificationRequest,
  IReassignCheckerForProjectDesignModificationRequest,
} from './designModification.models';

export const apiRoutes = {
  designModification: '/DesignModifications',
  designModificationSearch: '/DesignModifications/Search',
  exportDesignModificationSearchResult: '/DesignModifications/ExportExcel',
  exportDesignModificationSearchResultToPdf: '/DesignModifications/ExportPdf',
  designModificationRevision: (id: number) => `/DesignModifications/${id}/Revisions`,
  designModificationApprove: (id: number) => `/DesignModifications/${id}/Approve`,
  designModificationCreateAndIssueAsSrp: '/DesignModifications/CreateAndIssueAsSRP',
  designModificationCreateAndIssueAsTwc: '/DesignModifications/CreateAndApproveAsTWC',
  designModificationIssueAsSrp: '/DesignModifications/IssueAsSRP',
  designModificationIssueAsTwc: '/DesignModifications/IssueAsTWC',
  designModificationIssueAsDesigner: '/DesignModifications/IssueAsDesigner',
  designModificationCreateAndIssueAsDesigner: '/DesignModifications/CreateAndIssueAsDesigner',
  designModificationApproveAndCheckedAsLambeth: (id: number) => `/DesignModifications/${id}/ApproveAndCheckedAsLambeth`,
  designModificationReassignChecker: (id: number) => `/DesignModifications/${id}/ReassignChecker`,
  designModificationRequestEndorsement: (id: number) => `/DesignModifications/${id}/RequestEndorsement`,
  designModificationCancel: (id: number) => `/DesignModifications/${id}/Cancel`,
  downloadDesignModificationPdf: (id: number) => `/DesignModifications/${id}/Pdf`,
  downloadDesignModificationDesignerProjectDiscussionsPdf: (id: number) =>
    `/DesignModifications/${id}/ProjectDiscussions/Designer/Pdf`,
  downloadDesignModificationCheckerProjectDiscussionsPdf: (id: number) =>
    `/DesignModifications/${id}/ProjectDiscussions/Checker/Pdf`,
  designModificationOptionForDesignerTransmittal: `/DesignModifications/DesignerTransmittals/Options`,
  designModificationOptionsForLoadingRemovalCert: '/DesignModifications/LoadingRemovalCert/Options',
  downloadDesignModificationRfc: (id: number) => `/DesignModifications/${id}/Download`,
};

const issueDesignModificationFn: <RequestType>(
  apiRoute: string,
) => (request: RequestType) => Promise<IItemResponse<IProjectDesignModification>> =
  <RequestType>(apiRoute: string) =>
  async (request: RequestType): Promise<IItemResponse<IProjectDesignModification>> => {
    const result = await apiServerPost<IItemResponse<IProjectDesignModification>>(
      `${envConfigs.apiBaseUrl}${apiRoute}`,
      { ...request },
    );
    return result.data;
  };

export const createAndIssueDesignModificationAsSrp = issueDesignModificationFn<ICreateProjectDesignModificationRequest>(
  apiRoutes.designModificationCreateAndIssueAsSrp,
);

export const createAndIssueDesignModificationAsTwc = issueDesignModificationFn<ICreateProjectDesignModificationRequest>(
  apiRoutes.designModificationCreateAndIssueAsTwc,
);

export const createAndIssueDesignModificationAsDesigner =
  issueDesignModificationFn<ICreateProjectDesignModificationRequest>(
    apiRoutes.designModificationCreateAndIssueAsDesigner,
  );

export const issueDesignModificationAsSrp = issueDesignModificationFn<IUpdateProjectDesignModificationRequest>(
  apiRoutes.designModificationIssueAsSrp,
);

export const issueDesignModificationAsTwc = issueDesignModificationFn<IUpdateProjectDesignModificationRequest>(
  apiRoutes.designModificationIssueAsTwc,
);

export const issueDesignModificationAsDesigner = issueDesignModificationFn<IUpdateProjectDesignModificationRequest>(
  apiRoutes.designModificationIssueAsDesigner,
);

export const createDesignModificationAsync = issueDesignModificationFn<ICreateProjectDesignModificationRequest>(
  apiRoutes.designModification,
);

export const searchDesignModificationAsync = createSearchAsyncFn<
  IProjectDesignModificationSearchFilter,
  IProjectDesignModificationSearchResult
>(apiRoutes.designModificationSearch);

export interface IExportProjectDesignModificationProps extends IDownloadFileBaseProps {
  sortByQuery?: ISortByQuery;
  searchFilter: IProjectDesignModificationSearchFilter;
}

export const exportProjectDesignModificationSearchExcelAsync =
  createDownloadFileWithCustomPropsAsyncFn<IExportProjectDesignModificationProps>(
    apiRoutes.exportDesignModificationSearchResult,
  );

export const exportProjectDesignModificationSearchPdfAsync =
  createDownloadFileWithCustomPropsAsyncFn<IExportProjectDesignModificationProps>(
    apiRoutes.exportDesignModificationSearchResultToPdf,
  );

export const getDesignModificationAsync = async (
  id: number,
  props: IApiBaseProps,
): Promise<IItemResponse<IProjectDesignModification>> => {
  const result = await apiServerGet<IItemResponse<IProjectDesignModification>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.designModification}/${id}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export const getDesignModificationRevisionAsync = async (
  id: number,
  props: IApiBaseProps,
): Promise<IPagedResultsV1<IRevision>> => {
  const result = await apiServerGet<IPagedResultsV1<IRevision>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.designModificationRevision(id)}`,
    { params: props },
  );
  return result.data;
};

export const postDesignModificationRevisionAsync = async (
  id: number,
  props: IApiBaseProps,
): Promise<IItemResponse<IProjectDesignModification>> => {
  const result = await apiServerPost<IItemResponse<IProjectDesignModification>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.designModificationRevision(id)}`,
    null,
    { params: props },
  );
  return result.data;
};

export const putDesignModificationAsync = async (
  id: number,
  props: IUpdateProjectDesignModificationRequest,
): Promise<IItemResponse<IProjectDesignModification>> => {
  const result = await apiServerPut<IItemResponse<IProjectDesignModification>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.designModification}/${id}`,
    props,
  );
  return result.data;
};

export const putDesignModificationApproveAsync = async ({
  isRfc: isRFC,
  ...props
}: IApproveProjectDesignModificationRequest): Promise<IItemResponse<IProjectDesignModification>> => {
  const result = await apiServerPut<IItemResponse<IProjectDesignModification>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.designModificationApprove(props.id)}`,
    props,
    { params: { isRFC } },
  );
  return result.data;
};

export const putDesignModificationApproveAndCheckedAsLambethAsync = async (
  props: IUpdateProjectDesignModificationRequest,
): Promise<IItemResponse<IProjectDesignModification>> => {
  const result = await apiServerPut<IItemResponse<IProjectDesignModification>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.designModificationApproveAndCheckedAsLambeth(props.id)}`,
    props,
  );
  return result.data;
};

export const putDesignModificationReassignCheckerAsync = async (
  props: IReassignCheckerForProjectDesignModificationRequest,
): Promise<IItemResponse<IProjectDesignModification>> => {
  const result = await apiServerPut<IItemResponse<IProjectDesignModification>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.designModificationReassignChecker(props.id)}`,
    props,
  );
  return result.data;
};

export const putDesignModificationRequestEndorsementAsync = async (
  props: IUpdateProjectDesignModificationStatusRequest,
): Promise<IItemResponse<IProjectDesignModification>> => {
  const { id, ...rest } = props;
  const result = await apiServerPut<IItemResponse<IProjectDesignModification>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.designModificationRequestEndorsement(id)}`,
    undefined,
    { params: rest },
  );
  return result.data;
};

export const putDesignModificationCancelAsync = async (
  props: IUpdateProjectDesignModificationStatusRequest,
): Promise<IItemResponse<IProjectDesignModification>> => {
  const { id, ...rest } = props;
  const result = await apiServerPut<IItemResponse<IProjectDesignModification>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.designModificationCancel(id)}`,
    undefined,
    { params: rest },
  );
  return result.data;
};

export const downloadDesignModificationPdfAsync = async ({
  id,
  projectId,
  onDownloadProgressUpdate,
}: IPdfFileDownloadRequest): Promise<IFileResponse> => {
  return createDownloadFileAsyncFn(apiRoutes.downloadDesignModificationPdf(id), {
    id,
    projectId,
    onDownloadProgressUpdate,
  });
};

export const downloadDesignModificationDesignerProjectDiscussionsPdfAsync = async ({
  id,
  projectId,
  onDownloadProgressUpdate,
}: IPdfFileDownloadRequest): Promise<IFileResponse> => {
  return createDownloadFileAsyncFn(apiRoutes.downloadDesignModificationDesignerProjectDiscussionsPdf(id), {
    id,
    projectId,
    onDownloadProgressUpdate,
  });
};

export const downloadDesignModificationCheckerProjectDiscussionsPdfAsync = async ({
  id,
  projectId,
  onDownloadProgressUpdate,
}: IPdfFileDownloadRequest): Promise<IFileResponse> => {
  return createDownloadFileAsyncFn(apiRoutes.downloadDesignModificationCheckerProjectDiscussionsPdf(id), {
    id,
    projectId,
    onDownloadProgressUpdate,
  });
};

export const getDesignModificationOptionForDesignerTransmittalAsync = async (
  props: IProjectDesignModificationOptionForDesignerTransmittalRequest,
): Promise<IPagedResultsV1<IProjectDesignModificationOptionForDesignerTransmittal>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectDesignModificationOptionForDesignerTransmittal>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.designModificationOptionForDesignerTransmittal}`,
    { params: props },
  );
  return result.data;
};
export const getDesignModificationOptionsForLoadingRemovalCertAsync = async (
  props: ISearchDesignModificationDrawingRequest,
): Promise<IPagedResultsV1<IProjectDesignModification>> => {
  const result = await apiServerPost<IPagedResultsV1<IProjectDesignModification>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.designModificationOptionsForLoadingRemovalCert}`,
    props,
  );
  return result.data;
};

export interface IExportDesignModificationRfcProps extends IDownloadFileProgressProps {
  projectId: number;
  withCalReport: boolean;
  id: number;
}
export const exportDesignModificationRfcAsync =
  createDownloadFileWithCustomPropsAsyncFnGet<IExportDesignModificationRfcProps>((props) =>
    apiRoutes.downloadDesignModificationRfc(props.id),
  );
